import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Avatar, Tabs, Tab, Chip, Snackbar, Alert } from '@mui/material';
import {
  Star as StarIcon, People as PeopleIcon, Assessment as AssessmentIcon, HowToReg as ParticipationIcon,
} from '@mui/icons-material';
import { ratingAPI } from '../services/api';
import { errorText } from '../utils/format';
import { ScoreView, isStars } from './feedback/shared';
import ResultsTab from './feedback/ResultsTab';
import ParticipationTab from './feedback/ParticipationTab';
import QuestionsTab from './feedback/QuestionsTab';
import SettingsTab from './feedback/SettingsTab';

function StatCard({ icon, label, value, color, footer }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: `${color}18`, color, width: 52, height: 52 }}>{icon}</Avatar>
        <Box>
          <Box sx={{ minHeight: 34, display: 'flex', alignItems: 'center' }}>{typeof value === 'object' ? value : <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>}</Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {footer}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function RatingManagement() {
  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState([]);
  const [participation, setParticipation] = useState([]);
  const [overall, setOverall] = useState({ totalRatings: 0, totalTeachersRated: 0, overallAverage: 0 });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);

  const load = useCallback(async () => {
    try {
      const [c, s, p, d] = await Promise.all([ratingAPI.getConfig(), ratingAPI.getSettings(), ratingAPI.getParticipation(), ratingAPI.getDashboard()]);
      setConfig(c.data);
      setSettings(s.data);
      setParticipation(p.data);
      setOverall(d.data);
    } catch (e) { notify(errorText(e, 'Could not load Faculty Feedback'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const scaleType = config?.scaleType || 'STARS_5';
  const students = participation.reduce((a, p) => a + p.students, 0);
  const took = participation.reduce((a, p) => a + p.participated, 0);
  const partPct = students ? Math.round((took * 100) / students) : 0;
  const openBatches = settings.filter((s) => s.enabled).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h4">Faculty Feedback</Typography>
          <Typography variant="body2" color="text.secondary">Students rate their faculty; only the administration sees the results</Typography>
        </Box>
        {config && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip icon={<StarIcon />} label={isStars(scaleType) ? 'Rating style: stars (1-5)' : 'Rating style: score (1-10)'} color="primary" variant="outlined" />
            <Chip label={config.windowOpen ? (config.cycleName ? `Open: ${config.cycleName}` : 'Feedback window open') : (config.windowMessage || 'Closed')} color={config.windowOpen ? 'success' : 'default'} />
            {config.anonymous && <Chip label="Anonymous" />}
          </Box>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><StatCard icon={<AssessmentIcon />} label="Feedback forms received" value={overall.totalRatings} color="#00796b" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard icon={<PeopleIcon />} label="Faculty rated" value={overall.totalTeachersRated} color="#26a69a" /></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<StarIcon />} label="College average" color="#f9a825"
            value={overall.totalRatings ? <ScoreView avg10={overall.overallAverage} scaleType={scaleType} size={18} /> : '-'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ParticipationIcon />} label={`Students who took part (${openBatches} batch${openBatches === 1 ? '' : 'es'} open)`} value={`${partPct}%`} color="#0277bd"
            footer={<Typography variant="caption" color="text.secondary">{took} of {students} students</Typography>} />
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 2, borderBottom: '1px solid #eee' }}>
          <Tab label="Results" />
          <Tab label="Class participation" />
          <Tab label="Questions" />
          <Tab label="Settings" />
        </Tabs>
      </Paper>

      {tab === 0 && <ResultsTab config={config} settings={settings} notify={notify} />}
      {tab === 1 && <ParticipationTab config={config} settings={settings} participation={participation} reload={load} notify={notify} />}
      {tab === 2 && <QuestionsTab config={config} notify={notify} onChanged={load} />}
      {tab === 3 && <SettingsTab config={config} notify={notify} onSaved={load} />}

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
